import Helmet from 'react-helmet'
import PropTypes from 'prop-types'
import React from 'react'
import {connect} from 'react-redux'
import {createStructuredSelector} from 'reselect'

import * as crossSelectors from '../../store/cross'
import * as reviewsSelectors from '../../store/reviews'
import * as routerSelectors from '../../store/router'
import * as pdpSelectors from '../../store/productDetailPage'

const ProductRichSnippet = ({
  hasReviews,
  pathname,
  productAndModelImages,
  productBuyPrice,
  productDescription,
  productId,
  productIsPurchasable,
  productName,
  productRentPrice,
  reviewsAverageScore,
  seoDescription,
  soldOutEndDateForBuy,
  totalReviews,
  showBuyPathExperiment,
}) => {
  const ratingValue = hasReviews ? reviewsAverageScore.toFixed(1) : 0
  const ratingCount = hasReviews ? totalReviews : 0
  const baseSnippet = {
    '@context': 'http://schema.org/',
    '@type': 'Product',
    name: productName,
    image: productAndModelImages.map(({url}) => url),
    description: seoDescription || productDescription,
    brand: 'The Black Tux',
    ...(ratingCount > 0 && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: ratingValue.toString(),
        reviewCount: ratingCount.toString(),
      },
    }),
  }
  const baseOffer = {
    '@type': 'Offer',
    priceCurrency: 'USD',
    url: `${window.location.origin}${pathname}`,
  }
  const buySnippet = {
    ...baseSnippet,
    sku: `${productId}-buy`,
    offers: {
      ...baseOffer,
      availability:
        soldOutEndDateForBuy.length > 0
          ? 'https://schema.org/OutOfStock'
          : 'http://schema.org/InStock',
      price: productBuyPrice,
    },
  }
  const rentSnippet = {
    ...baseSnippet,
    sku: `${productId}-rent`,
    offers: {
      ...baseOffer,
      availability: 'http://schema.org/InStock',
      businessFunction: 'http://purl.org/goodrelations/v1#LeaseOut',
      price: productRentPrice,
    },
  }

  return (
    <Helmet>
      {!showBuyPathExperiment && (
        <script type="application/ld+json">{JSON.stringify(rentSnippet)}</script>
      )}
      {productIsPurchasable && (
        <script type="application/ld+json">{JSON.stringify(buySnippet)}</script>
      )}
    </Helmet>
  )
}

ProductRichSnippet.defaultProps = {
  productName: undefined,
  productRentPrice: undefined,
  seoDescription: undefined,
}

ProductRichSnippet.propTypes = {
  hasReviews: PropTypes.bool.isRequired,
  pathname: PropTypes.string.isRequired,
  productAndModelImages: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string.isRequired,
    }),
  ).isRequired,
  productBuyPrice: PropTypes.string,
  productDescription: PropTypes.string.isRequired,
  productId: PropTypes.string.isRequired,
  productIsPurchasable: PropTypes.bool.isRequired,
  productName: PropTypes.string,
  productRentPrice: PropTypes.string,
  reviewsAverageScore: PropTypes.number.isRequired,
  seoDescription: PropTypes.string,
  soldOutEndDateForBuy: PropTypes.string.isRequired,
  totalReviews: PropTypes.number.isRequired,
  showBuyPathExperiment: PropTypes.bool,
}

const mapStateToProps = createStructuredSelector({
  hasReviews: reviewsSelectors.hasReviews,
  pathname: routerSelectors.pathname,
  productAndModelImages: pdpSelectors.productAndModelImages,
  productBuyPrice: pdpSelectors.productBuyPrice,
  productDescription: pdpSelectors.productDescription,
  productId: pdpSelectors.productId,
  productIsPurchasable: pdpSelectors.productIsPurchasable,
  productName: pdpSelectors.productName,
  productRentPrice: pdpSelectors.productRentPrice,
  reviewsAverageScore: reviewsSelectors.averageScore,
  seoDescription: pdpSelectors.productSeoDescription,
  soldOutEndDateForBuy: crossSelectors.soldOutEndDateForBuy,
  totalReviews: reviewsSelectors.totalReviews,
})

export default connect(mapStateToProps)(ProductRichSnippet)
